import { useState, useCallback } from "react";
import { useAuthStore } from "@/store/authStore";
import { useMenuItemForm } from "@/hooks/useMenuItemForm";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { getMenuInventoryRelationships } from "@/services/inventory/inventoryService";

// Import sub-components
import { MenuItemBasicInfo } from "./form/MenuItemBasicInfo";
import { MenuItemDescription } from "./form/MenuItemDescription";
import { MenuItemCategories } from "./form/MenuItemCategories";
import { MenuItemTags } from "./form/MenuItemTags";
import { MenuItemImage } from "./form/MenuItemImage";
import { AvailabilityToggle } from "./form/AvailabilityToggle";
import { FormButtons } from "./form/FormButtons";
import { MenuItemFormData } from "@/types/menu";

interface MenuItemFormProps {
  initialData?: {
    id?: string;
    name: string;
    description: string;
    price: string;
    available: boolean;
    image_url?: string;
    store_id: string;
    categoryIds: string[];
    tags: string[];
  };
  onSubmit: (data: MenuItemFormData) => void;
  submitButtonText: string;
  isSubmitting: boolean;
}

export function MenuItemForm({
  initialData = {
    name: "",
    description: "",
    price: "",
    available: true,
    image_url: "",
    store_id: "",
    categoryIds: [],
    tags: [],
  },
  onSubmit,
  submitButtonText,
  isSubmitting,
}: MenuItemFormProps) {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);
  const [existingInventoryRelationships, setExistingInventoryRelationships] =
    useState<any[]>([]);

  const {
    formData,
    errors,
    uploadedImage,
    availableTags,
    hasErrors,
    handleChange,
    setUploadedImage,
    validateForm,
    getFormData,
  } = useMenuItemForm({ initialData });

  // Memoize the checkStoreInventoryMode function
  const checkStoreInventoryMode = useCallback(async (storeId: string) => {
    try {
      const { data, error } = await supabase
        .from("stores")
        .select("advanced_inventory_tracking")
        .eq("id", storeId)
        .single();

      if (error) throw error;

      setIsAdvancedMode(data?.advanced_inventory_tracking || false);
    } catch (error) {
      console.error("Error checking store inventory mode:", error);
      setIsAdvancedMode(false);
    }
  }, []);

  // Check if the selected store has advanced inventory mode enabled
  // useEffect(() => {
  //   if (formData.store_id) {
  //     checkStoreInventoryMode(formData.store_id);
  //   }
  // }, [formData.store_id, checkStoreInventoryMode]);

  // Memoize the fetchExistingInventoryRelationships function
  const fetchExistingInventoryRelationships = useCallback(
    async (menuItemId: string) => {
      try {
        const relationships = await getMenuInventoryRelationships(menuItemId);
        setExistingInventoryRelationships(relationships);
      } catch (error) {
        console.error(
          "Error fetching existing inventory relationships:",
          error
        );
      }
    },
    []
  );

  // Fetch existing inventory relationships when editing an item
  // useEffect(() => {
  //   if (initialData.id) {
  //     fetchExistingInventoryRelationships(initialData.id);
  //   }
  // }, [initialData.id, isAdvancedMode, fetchExistingInventoryRelationships]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Get the form data
      const finalFormData = getFormData();

      // Process the form submission
      onSubmit(finalFormData as MenuItemFormData);
    } catch (error) {
      console.error("Error submitting menu item:", error);
      throw error;
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 py-2">
      <div className="space-y-4">
        <MenuItemBasicInfo
          name={formData.name}
          price={formData.price}
          onNameChange={(value) => handleChange("name", value)}
          onPriceChange={(value) => handleChange("price", value)}
          nameError={errors.name}
          priceError={errors.price}
        />

        <MenuItemDescription
          description={formData.description}
          onDescriptionChange={(value) => handleChange("description", value)}
        />

        <MenuItemCategories
          storeId={formData.store_id || ""}
          selectedCategoryIds={formData.categoryIds}
          onCategoriesChange={(ids) => handleChange("categoryIds", ids)}
          error={errors.categoryIds}
        />

        <MenuItemTags
          tags={formData.tags}
          availableTags={availableTags}
          onTagsChange={(tags) => handleChange("tags", tags)}
        />

        <MenuItemImage
          initialImage={initialData.image_url}
          onImageUploaded={setUploadedImage}
        />

        <AvailabilityToggle
          checked={formData.available}
          onCheckedChange={(checked) => handleChange("available", checked)}
        />
      </div>

      <FormButtons
        isSubmitting={isSubmitting}
        submitButtonText={submitButtonText}
        hasErrors={hasErrors}
      />
    </form>
  );
}
